import styled from '@emotion/styled';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import { useAnonymousAnalyticsTrackPage } from '../../common/analytics';
import { IconArrowRight } from '../../common/design-tokens/Icons';
import { validateEmail } from '../../common/utils';
import FloatingLabelInput from '../shared/FloatingLabelInput';
import { translateError } from './authenticationUtils';
import InvitationAuthenticationPageBase from './InvitationAuthenticationPageBase';
import PasswordField from './PasswordField';
import { Inputs, SubmitButton } from './styledComponents';

const MIN_PASSWORD_LENGTH = 6;

const CreateAccountIcon = styled(IconArrowRight)`
  svg {
    path {
      fill: white;
    }
  }
`;

function passwordsMatch({
  password: p1,
  passwordConfirmation: p2,
}: {
  password: string;
  passwordConfirmation: string;
}) {
  return !!p1 && !!p2 && p1 === p2;
}

function passwordLengthOk(password: string) {
  return password.length > MIN_PASSWORD_LENGTH;
}

export default function SignUp() {
  return (
    <InvitationAuthenticationPageBase type="signup">
      {SignUpInner}
    </InvitationAuthenticationPageBase>
  );
}

export function SignUpInner({
  token,
  error,
  xsrf,
}: {
  token: string;
  error: string | null;
  xsrf: string;
}) {
  useAnonymousAnalyticsTrackPage({
    name: 'Invitation Sign Up',
    category: 'Public',
    properties: {
      error,
    },
  });

  const [email, setEmail] = useState('');
  const [passwords, setPasswords] = useState({
    password: '',
    passwordConfirmation: '',
  });
  const [showPasswords, setShowPasswords] = useState(false);

  const [passwordErrors, setPasswordErrors] = useState<{
    [k in keyof typeof passwords]?: string;
  }>({});

  const [validEmailAddress, setValidEmailAddress] = useState<
    boolean | undefined
  >(undefined);

  const intl = useIntl();

  const hasEmailError = error === 'invalid_email' || error === 'email_in_use';

  const hasPasswordError = error === 'password_too_weak';

  const getEmailErrorMessage =
    error === 'invalid_email'
      ? translateError('invalid_email')
      : translateError('email_in_use');

  function validateEmailAddress(passedEmail: string) {
    if (passedEmail === '') {
      setValidEmailAddress(undefined);
    } else {
      setValidEmailAddress(validateEmail(passedEmail));
    }
  }

  function validatePasswordsMatch(passedPasswords: {
    password: string;
    passwordConfirmation: string;
  }) {
    if (passwordsMatch(passedPasswords)) {
      setPasswordErrors({
        password: undefined,
        passwordConfirmation: undefined,
      });
    } else {
      setPasswordErrors({ passwordConfirmation: 'Passwords do not match' });
    }
  }

  function validatePasswordLength(
    field: 'password' | 'passwordConfirmation',
    password: string,
  ) {
    if (!passwordLengthOk(password)) {
      setPasswordErrors({ ...passwordErrors, [field]: 'Too short' });
    }
  }

  const errorsInForm =
    !validEmailAddress ||
    !passwordLengthOk(passwords.password) ||
    !passwordsMatch(passwords);

  return (
    <form method="post" action="/api/account/signup">
      <input type="hidden" name="token" value={token} />
      <input type="hidden" name="_xsrf" value={xsrf} />
      <FloatingLabelInput
        key="email"
        name="email"
        value={email}
        error={
          validEmailAddress === false
            ? 'Not valid email'
            : hasEmailError
              ? getEmailErrorMessage
              : undefined
        }
        inputProps={{
          'aria-invalid': validEmailAddress === false,
          autoComplete: 'email',
        }}
        label={intl.formatMessage({
          id: 'general_email_fieldtext',
        })}
        data-testid="signup-email"
        onChange={(event) => {
          setEmail(event.target.value);
          validateEmailAddress(event.target.value);
        }}
      />

      <Inputs>
        {(['password', 'passwordConfirmation'] as const).map((field) => (
          <PasswordField
            key={field}
            name={field}
            value={passwords[field]}
            error={
              hasPasswordError
                ? translateError('password_too_weak')
                : passwordErrors[field]
            }
            inputProps={{
              minLength: MIN_PASSWORD_LENGTH,
              'aria-invalid': !!passwordErrors[field],
              autoComplete: 'new-password',
            }}
            label={intl.formatMessage({
              id:
                field === 'password'
                  ? 'general_password_fieldtext'
                  : 'general_confirm_password_fieldtext',
            })}
            data-testid={`signup-${field}`}
            onChange={(event) => {
              const updatedPasswords = {
                ...passwords,
                [field]: event.target.value,
              };
              setPasswords(updatedPasswords);
              validatePasswordsMatch(updatedPasswords);
              validatePasswordLength(field, updatedPasswords[field]);
            }}
            showPassword={showPasswords}
            toggleShowPassword={() => setShowPasswords((state) => !state)}
          />
        ))}
      </Inputs>

      <SubmitButton
        size="xsmall"
        rounded
        icon="end"
        type="submit"
        disabled={errorsInForm}
      >
        Create account
        <CreateAccountIcon aria-hidden />
      </SubmitButton>
    </form>
  );
}
